// src/styles/LayoutStyles.js
import styled from 'styled-components';
import { Logo as CommonLogo } from './CommonStyles';

export const PageWrapper = styled.div`
    min-height: 100vh;
    display: flex;
    flex-direction: column;
`;

export const AppContainer = styled.div`
    display: flex;
    flex-grow: 1;
    font-family: ${({ theme }) => theme.fonts.main};
    position: relative;
    overflow: hidden;
    height: 100vh; // Dodajemy, aby AppContainer zajmował całą wysokość
`;

export const Sidebar = styled.aside`
    background-color: ${({ theme }) => theme.colors.backgroundLight};
    color: ${({ theme }) => theme.colors.textMedium};
    width: 260px;
    padding: 0;
    display: flex;
    flex-direction: column;
    box-shadow: ${({ theme }) => theme.shadows.lg};
    z-index: 1000;
    flex-shrink: 0;
    height: 100%;
    transition: transform 0.3s ease-in-out, width 0.3s ease-in-out;

    @media (max-width: 768px) {
        position: fixed;
        inset-block-start: 0;
        inset-inline-start: 0;
        height: 100vh;
        transform: ${({ isOpen }) => (isOpen ? 'translateX(0)' : 'translateX(-100%)')};
        border-right: 1px solid ${({ theme }) => theme.colors.borderColor};
    }
`;

export const SidebarHeader = styled.div`
    padding: ${({ theme }) => theme.spacing.lg} ${({ theme }) => theme.spacing.lg} ${({ theme }) => theme.spacing.md};
    text-align: center;
    flex-shrink: 0;

    & > a {
        text-decoration: none;
        display: inline-block;
        margin-bottom: ${({ theme }) => theme.spacing.xs};
    }

    ${CommonLogo} {
        h1, span {
            color: ${({ theme }) => theme.colors.primaryDarker};
            font-size: 1.15rem;
        }
        svg {
            color: ${({ theme }) => theme.colors.primary};
            width: 30px;
            height: 30px;
        }
    }

    p.app-subtitle {
        font-family: ${({ theme }) => theme.fonts.main};
        font-size: 0.8rem;
        color: ${({ theme }) => theme.colors.textMedium};
        margin-top: 0.25rem;
        margin-bottom: ${({ theme }) => theme.spacing.sm};
    }
`;

export const UserInfoWrapper = styled.div`
    padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
    margin: 0 ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.md};
    background-color: ${({ theme }) => theme.colors.primaryLightest};
    border-radius: ${({ theme }) => theme.borderRadius};
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing.sm};
    color: ${({ theme }) => theme.colors.primaryDark};
    flex-shrink: 0;

    img.user-avatar {
        width: 36px;
        height: 36px;
        border-radius: 50%;
        object-fit: cover;
        border: 2px solid ${({ theme }) => theme.colors.primaryLight};
    }

    .user-details {
        line-height: 1.3;
        text-align: left;
        overflow: hidden;
    }

    .username {
        font-weight: 600;
        font-size: 0.9rem;
        color: ${({ theme }) => theme.colors.primaryDarker};
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .user-email {
        font-size: 0.75rem;
        color: ${({ theme }) => theme.colors.textMedium};
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .guest-actions {
        width: 100%;
        text-align: center;
        p {
            font-size: 0.8rem;
            color: ${({ theme }) => theme.colors.textMedium};
            margin-bottom: ${({ theme }) => theme.spacing.xs};
            a {
                color: ${({ theme }) => theme.colors.primaryDark};
                font-weight: 600;
            }
        }
        button {
            font-size: 0.85rem;
            padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
            width: 100%;
            margin-bottom: ${({ theme }) => theme.spacing.xs};
            border-radius: ${({ theme }) => theme.buttonBorderRadius};
        }
    }
`;

export const Nav = styled.nav`
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    overflow-y: auto;
    padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.md};
`;

export const NavButtonStyled = styled.button`
    width: 100%;
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing.sm};
    padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
    border-radius: ${({ theme }) => theme.borderRadius};
    transition: background-color 0.2s ease-in-out, color 0.2s ease-in-out, transform 0.1s ease-out, font-weight 0.2s ease-in-out;
    text-align: left;
    font-family: ${({ theme }) => theme.fonts.headings};
    font-weight: ${({ $active }) => ($active ? '600' : '500')};
    font-size: 0.9rem;
    letter-spacing: 0.2px;
    background-color: transparent;
    color: ${({ theme }) => theme.colors.textMedium};
    border: none;

    svg {
        color: ${({ theme }) => theme.colors.textMedium};
        transition: color 0.2s ease-in-out;
        flex-shrink: 0;
        width: 20px;
        height: 20px;
    }

    span {
        flex-grow: 1;
        font-size: inherit;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    &:not([disabled]):hover {
        background-color: ${({ theme, $active }) => !$active && theme.colors.neutralLight};
        color: ${({ theme, $active }) => !$active && theme.colors.primaryDark};
        svg {
            color: ${({ theme, $active }) => !$active && theme.colors.primary};
        }
    }

    ${({ $active, theme }) => $active && `
        background-color: ${theme.colors.primaryLightest};
        color: ${theme.colors.primaryDark};
        svg {
            color: ${theme.colors.primary};
        }
    `}

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
        background-color: transparent !important;
        color: ${({ theme }) => theme.colors.textDisabled} !important;
        svg {
            color: ${({ theme }) => theme.colors.textDisabled} !important;
        }
    }
`;

export const SidebarFooter = styled.div`
    padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg};
    margin-top: auto;
    border-top: 1px solid ${({ theme }) => theme.colors.borderColor};
    flex-shrink: 0;

    ${NavButtonStyled} {
        font-weight: 500;
        font-size: 0.9rem;
        background-color: transparent;
        color: ${({ theme }) => theme.colors.textMedium};
        &:hover:not(:disabled) {
            background-color: ${({ theme }) => theme.colors.dangerBg} !important;
            color: ${({ theme }) => theme.colors.danger} !important;
            svg {
                color: ${({ theme }) => theme.colors.danger} !important;
            }
        }
    }

    p.copyright-text {
        font-family: ${({ theme }) => theme.fonts.main};
        font-size: 0.75rem;
        color: ${({ theme }) => theme.colors.textDisabled};
        text-align: center;
        margin-top: ${({ theme }) => theme.spacing.sm};
    }
`;

export const MainContent = styled.main`
    flex: 1;
    background-color: ${({ theme }) => theme.colors.background};
    display: flex;
    flex-direction: column;
    overflow: hidden;
`;

export const MobileMenuButton = styled.button`
    display: none;
    @media (max-width: 768px) {
        display: flex;
        align-items: center;
        justify-content: center;
        position: fixed;
        top: 8px;
        left: 8px;
        z-index: 1010;
        padding: 0;
        background-color: ${({ theme }) => theme.colors.white};
        color: ${({ theme }) => theme.colors.primaryDark};
        border-radius: 50%;
        width: 44px;
        height: 44px;
        box-shadow: ${({ theme }) => theme.shadows.lg};
        border: 1px solid ${({ theme }) => theme.colors.borderColor};
        svg {
            width: 22px;
            height: 22px;
        }
    }
`;

export const MobileOverlay = styled.div`
    display: none;
    @media (max-width: 768px) {
        display: ${({ isOpen }) => (isOpen ? 'block' : 'none')};
        position: fixed;
        inset: 0;
        background-color: rgba(0, 0, 0, 0.5);
        z-index: 999;
    }
`;

export const PageContentWrapper = styled.div`
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    width: 100%;
    overflow: hidden; // To ma być

    &:not(.no-padding-or-scroll-managed-internally) {
        padding: ${({ theme }) => theme.spacing.lg};
        overflow-y: auto;
        -webkit-overflow-scrolling: touch;

        // **KLUCZOWA ZMIANA JEST TUTAJ**
        @media (max-width: 768px) {
            padding: ${({ theme }) => theme.spacing.md};
            padding-top: calc(${({ theme }) => theme.spacing.md} + 5px);
            padding-bottom: 80px; // <-- Dodajemy padding na dole
        }
        
        @media (max-width: 480px) {
            padding-left: ${({ theme }) => theme.spacing.sm};
            padding-right: ${({ theme }) => theme.spacing.sm};
        }
    }
`;

export const PageHeader = styled.h2`
    font-family: ${({ theme }) => theme.fonts.headings};
    font-size: 1.75rem;
    font-weight: 700;
    color: ${({ theme }) => theme.colors.primaryDarker};
    margin-bottom: ${({ theme }) => theme.spacing.md};
    flex-shrink: 0;

    @media (max-width: 768px) {
        font-size: 1.2rem;
        margin-bottom: ${({ theme }) => theme.spacing.sm};
        margin-left: 10px;
        text-align: center;
    }
`;

export const PageDescription = styled.p`
    font-family: ${({ theme }) => theme.fonts.main};
    color: ${({ theme }) => theme.colors.textMedium};
    margin-bottom: ${({ theme }) => theme.spacing.lg};
    font-size: 0.95rem;
    line-height: 1.6;
    flex-shrink: 0;

    @media (max-width: 768px) {
        font-size: 0.875rem;
        margin-bottom: ${({ theme }) => theme.spacing.md};
    }
`;